import type { Platform } from './enums.js';

/**
 * The fraud rules, as numbers, and the arithmetic that applies them.
 *
 * NO ZOD HERE, like `submissions.ts` and for the same reason: this file is
 * plain types, constants and pure functions, so the web app can explain a hold
 * to a creator without the server's request schemas following it into the
 * browser bundle. `fraudThresholdsSchema` lives in `./contracts.ts`.
 *
 * ## Where these defaults come from
 *
 * They are MEASURED, on the fleet's own 43,729 view snapshots
 * (`post_stat_snapshots` in Amplify's database, read-only, 2026-09-21). The
 * sample is the 200 richest curves: every post that carries a reading in the
 * 48-hour window AND one in the 72-hour window, ranked by whether it also has
 * a 6 h and a 24 h reading — 83 Instagram, 64 TikTok, 53 YouTube. A reading is
 * attributed to hour H the way `contentSignal.ts` attributes one, newest
 * reading in `[0.5H, H]`, so a two-hour-old count is never read as a six-hour
 * one.
 *
 * What that sample says, and what each default is therefore set to:
 *
 * | measured | Instagram | TikTok | YouTube |
 * | --- | --- | --- | --- |
 * | share of 72 h views arriving after hour 48, median | 0.000 | 0.003 | 0.010 |
 * | the same, p90 | 0.050 | 0.096 | (noise) |
 * | the same, p99 / max | 0.273 / 0.643 | 0.227 / 0.324 | (noise) |
 * | the same, max among posts over 200 views | 0.239 | 0.324 | 0.087 |
 * | growth ratio across ONE late bucket, max | 3.25 | 1.48 | 6.93 |
 * | growth ratio 6 h → 24 h, max | 22.3 | 4.03 | 5.00 |
 * | (likes + comments) / views, median | 0.000 | 0.017 | 0.010 |
 *
 * Three things follow, and they are the whole design:
 *
 * 1. **Ratios are meaningless on small numbers.** The fleet's median post
 *    finishes at 145 (IG) / 167 (TT) / 3 (YT) views; on a post that ends at
 *    four views, one late view is a 25% "late share". Every ratio rule is
 *    therefore gated on `minViewsForRatioChecks` and simply passes below it.
 *    THE FLOOR IS 1,000 AND THE REPLAY IS WHY: at 500 the engagement rule held
 *    exactly one of the 200 real curves — a TikTok post that finished at 920
 *    views with zero likes and zero comments, which is an ordinary fleet post
 *    and not fraud at all. One thousand views is also the unit the money is
 *    quoted in, so below it a post is worth less than one whole per-thousand
 *    rate and there is nothing at stake either way.
 * 2. **The late-share thresholds sit above the largest honest value measured**
 *    (0.24 IG / 0.32 TT over the floor), because the cost of a false HOLD is a
 *    real creator waiting on a human, and the thing being caught — bought
 *    views delivered after the window — arrives in the 0.5–1.0 range, not at
 *    0.3. YouTube gets more room because its curve is genuinely the slowest of
 *    the three (about 48% of its 24-hour views by hour 24, against Instagram's
 *    94% of its 48-hour views by hour 6).
 * 3. **Velocity ignores the 6 h → 24 h step.** A 22× jump there is what a real
 *    hit looks like on Instagram, and holding every viral post is how a
 *    campaign loses the creators it wants. Only steps whose start is at or
 *    after hour 24 are examined, where the largest honest jump measured was
 *    3.25×.
 *
 * All of them are per-campaign overridable (`Campaign.fraudThresholdsJson`)
 * because, as `contentSignal.ts` says about its own numbers, they are a
 * property of the accounts being measured and not a law of the platforms.
 */

/* ------------------------------------------------------------------ */
/* The shape                                                           */
/* ------------------------------------------------------------------ */

export interface PlatformFraudThresholds {
  /**
   * CURVE_SHAPE. The share of the verified figure that arrived after hour 48,
   * above which the earning is held: `(views72 - views48) / views72`.
   */
  maxLateViewShare: number;
  /**
   * ENGAGEMENT_RATIO. `(likes + comments) / views` floor. A post that is watched
   * by people collects some of both; bought views collect neither.
   */
  minEngagementPerView: number;
  /**
   * VELOCITY. The largest multiple by which views may grow across ONE bucket
   * step that starts at or after hour 24.
   */
  maxLateBucketGrowthRatio: number;
}

export interface FraudThresholds {
  /**
   * Below this many verified views the three ratio rules above do not run at
   * all: they would be deciding about money that rounds to nothing, on numbers
   * where one view moves the ratio by a percentage point.
   */
  minViewsForRatioChecks: number;
  /** Per-platform, because the three curves are measurably different shapes. */
  perPlatform: Record<Platform, PlatformFraudThresholds>;
  /**
   * CONCENTRATION. One creator's share of the campaign's verified views, above
   * which their next calculation is held for a person.
   */
  maxCreatorViewShare: number;
  /**
   * How many creators must already have a completed calculation on the campaign
   * before CONCENTRATION means anything. The first creator to finish holds 100%
   * of a campaign's verified views by arithmetic, not by fraud.
   */
  concentrationMinCreators: number;
  /**
   * HIGH_VALUE. A single post whose verified figure is at or above this is held
   * for a human regardless of every other rule.
   */
  holdAboveVerifiedViews: number;
}

/**
 * The measured defaults. See the table above for where each number comes from.
 *
 * `minEngagementPerView` is the weakest-evidenced of them and is deliberately
 * set an order of magnitude BELOW the measured medians: the fleet's own
 * Instagram snapshots carry `likes = 0` on 1,274 of 2,074 readings, so a floor
 * derived from that sample would either be zero (and never fire) or would hold
 * honest posts. One like per thousand views only ever fires on the pathological
 * case — thousands of views and literally nobody reacting — which is exactly
 * the bought-views signature. The plan says these get calibrated on real
 * traffic during the human-reviewed pilot; this one most of all.
 */
export const DEFAULT_FRAUD_THRESHOLDS: FraudThresholds = Object.freeze({
  minViewsForRatioChecks: 1_000,
  perPlatform: Object.freeze({
    INSTAGRAM: Object.freeze({
      maxLateViewShare: 0.35,
      minEngagementPerView: 0.001,
      maxLateBucketGrowthRatio: 4,
    }),
    TIKTOK: Object.freeze({
      maxLateViewShare: 0.35,
      minEngagementPerView: 0.001,
      maxLateBucketGrowthRatio: 4,
    }),
    YOUTUBE: Object.freeze({
      maxLateViewShare: 0.4,
      minEngagementPerView: 0.002,
      maxLateBucketGrowthRatio: 4,
    }),
  }),
  maxCreatorViewShare: 0.5,
  concentrationMinCreators: 4,
  holdAboveVerifiedViews: 25_000,
}) as FraudThresholds;

/** The hour at which a bucket step stops being "the post taking off". */
export const VELOCITY_FIRST_BUCKET_HOURS = 24;

/** The hour after which arriving views are "late" for CURVE_SHAPE. */
export const LATE_VIEWS_AFTER_HOURS = 48;

/* ------------------------------------------------------------------ */
/* Merging an operator's overrides over the defaults                   */
/* ------------------------------------------------------------------ */

const PLATFORM_KEYS: readonly Platform[] = ['TIKTOK', 'INSTAGRAM', 'YOUTUBE'];

const num = (value: unknown, fallback: number, min: number, max: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max
    ? value
    : fallback;

/**
 * Read `Campaign.fraudThresholdsJson` over the defaults.
 *
 * PARTIAL OVERRIDES, by design: a campaign that wants a different Instagram
 * curve threshold should not have to restate the other eleven numbers, because
 * a restated number is a number that silently stops tracking the default when
 * the default is recalibrated. Anything missing, malformed or out of range
 * falls back to the measured default rather than to zero — a zero threshold
 * would hold every submission on the campaign, which is the one failure mode
 * that looks like the system working.
 */
export function fraudThresholdsFrom(raw: unknown): FraudThresholds {
  const input = (raw ?? {}) as Partial<FraudThresholds> & {
    perPlatform?: Partial<Record<Platform, Partial<PlatformFraudThresholds>>>;
  };
  const perPlatform = {} as Record<Platform, PlatformFraudThresholds>;
  for (const platform of PLATFORM_KEYS) {
    const base = DEFAULT_FRAUD_THRESHOLDS.perPlatform[platform];
    const over: Partial<PlatformFraudThresholds> = input.perPlatform?.[platform] ?? {};
    perPlatform[platform] = {
      maxLateViewShare: num(over.maxLateViewShare, base.maxLateViewShare, 0, 1),
      minEngagementPerView: num(over.minEngagementPerView, base.minEngagementPerView, 0, 1),
      maxLateBucketGrowthRatio: num(
        over.maxLateBucketGrowthRatio,
        base.maxLateBucketGrowthRatio,
        1,
        1_000_000,
      ),
    };
  }
  return {
    minViewsForRatioChecks: num(
      input.minViewsForRatioChecks,
      DEFAULT_FRAUD_THRESHOLDS.minViewsForRatioChecks,
      0,
      1_000_000_000,
    ),
    perPlatform,
    maxCreatorViewShare: num(
      input.maxCreatorViewShare,
      DEFAULT_FRAUD_THRESHOLDS.maxCreatorViewShare,
      0,
      1,
    ),
    concentrationMinCreators: num(
      input.concentrationMinCreators,
      DEFAULT_FRAUD_THRESHOLDS.concentrationMinCreators,
      1,
      10_000,
    ),
    holdAboveVerifiedViews: num(
      input.holdAboveVerifiedViews,
      DEFAULT_FRAUD_THRESHOLDS.holdAboveVerifiedViews,
      1,
      1_000_000_000,
    ),
  };
}

/* ------------------------------------------------------------------ */
/* The rules themselves, as pure functions over a series               */
/* ------------------------------------------------------------------ */

/** One reading, reduced to what a rule needs. */
export interface CurvePoint {
  /** The bucket this reading serves: 6, 24, 48, 72, or the final one. */
  bucketHours: number;
  /** NULL is NOT OBSERVED and never 0; a rule that needs a number skips it. */
  views: number | null;
  likes: number | null;
  comments: number | null;
}

export type FraudVerdict =
  | { result: 'PASS'; note: string; evidence: Record<string, unknown> }
  | { result: 'FAIL'; note: string; evidence: Record<string, unknown> }
  | { result: 'EXCEPTION'; note: string; evidence: Record<string, unknown> };

const at = (series: readonly CurvePoint[], hours: number): CurvePoint | undefined =>
  series.find((p) => p.bucketHours === hours && p.views !== null);

/** Every reading with a number, oldest bucket first. */
const measured = (series: readonly CurvePoint[]): CurvePoint[] =>
  [...series].filter((p) => p.views !== null).sort((a, b) => a.bucketHours - b.bucketHours);

const pct = (value: number): string => `${(value * 100).toFixed(1)}%`;

/**
 * CURVE_SHAPE — the share of the verified figure that arrived after hour 48.
 *
 * Across 4,082 of the fleet's own posts the MEDIAN growth after 48 hours is
 * exactly 1.000: honest reach is finished by then. A post that collects a third
 * of its views in the last day is not a post the platform kept showing; it is a
 * post somebody bought views for, and the delivery schedule is the tell.
 */
export function evaluateCurveShape(
  series: readonly CurvePoint[],
  verifiedViews: number,
  limits: PlatformFraudThresholds,
  minViews: number,
): FraudVerdict {
  const early = at(series, LATE_VIEWS_AFTER_HOURS);
  const evidence: Record<string, unknown> = {
    threshold: limits.maxLateViewShare,
    lateAfterHours: LATE_VIEWS_AFTER_HOURS,
    verifiedViews,
    viewsAt48h: early?.views ?? null,
    minViewsForRatioChecks: minViews,
  };

  if (verifiedViews < minViews) {
    return {
      result: 'PASS',
      note: `Under ${minViews.toLocaleString('en-US')} views, so the shape of the curve is not evidence about anything.`,
      evidence: { ...evidence, skipped: 'below_view_floor' },
    };
  }
  if (!early || early.views === null) {
    return {
      result: 'EXCEPTION',
      note: 'There is no 48-hour reading, so how much of this arrived late cannot be told.',
      evidence: { ...evidence, skipped: 'no_48h_reading' },
    };
  }
  if (verifiedViews <= 0) {
    return { result: 'PASS', note: 'No views to apportion.', evidence };
  }

  const lateShare = (verifiedViews - early.views) / verifiedViews;
  const rounded = Math.round(lateShare * 10_000) / 10_000;
  if (lateShare > limits.maxLateViewShare) {
    return {
      result: 'FAIL',
      note:
        `${pct(lateShare)} of the views arrived after hour 48, against a ${pct(limits.maxLateViewShare)} limit. ` +
        'Real reach on this platform is finished by then, so this is held for a person to look at.',
      evidence: { ...evidence, lateShare: rounded },
    };
  }
  return {
    result: 'PASS',
    note: `${pct(lateShare)} of the views arrived after hour 48, inside the ${pct(limits.maxLateViewShare)} limit.`,
    evidence: { ...evidence, lateShare: rounded },
  };
}

/** ENGAGEMENT_RATIO — likes and comments per view against a platform floor. */
export function evaluateEngagementRatio(
  series: readonly CurvePoint[],
  verifiedViews: number,
  limits: PlatformFraudThresholds,
  minViews: number,
): FraudVerdict {
  const last = measured(series).at(-1);
  const evidence: Record<string, unknown> = {
    threshold: limits.minEngagementPerView,
    verifiedViews,
    likes: last?.likes ?? null,
    comments: last?.comments ?? null,
  };

  if (verifiedViews < minViews) {
    return {
      result: 'PASS',
      note: `Under ${minViews.toLocaleString('en-US')} views, so the engagement ratio is noise.`,
      evidence: { ...evidence, skipped: 'below_view_floor' },
    };
  }
  /* NULL IS NOT ZERO. An Instagram photo publishes no like count and a YouTube
     owner can hide theirs; treating that as "nobody reacted" would hold an
     honest post because of a provider's response shape. */
  if (!last || (last.likes === null && last.comments === null)) {
    return {
      result: 'EXCEPTION',
      note: 'The platform published no like or comment count for this post, so the ratio could not be worked out.',
      evidence: { ...evidence, skipped: 'no_engagement_published' },
    };
  }

  const engagement = (last.likes ?? 0) + (last.comments ?? 0);
  const ratio = verifiedViews > 0 ? engagement / verifiedViews : 0;
  const rounded = Math.round(ratio * 1_000_000) / 1_000_000;
  if (ratio < limits.minEngagementPerView) {
    return {
      result: 'FAIL',
      note:
        `${engagement.toLocaleString('en-US')} likes and comments against ${verifiedViews.toLocaleString('en-US')} views ` +
        `(${rounded}), below the ${limits.minEngagementPerView} floor for this platform.`,
      evidence: { ...evidence, engagement, ratio: rounded },
    };
  }
  return {
    result: 'PASS',
    note: `${engagement.toLocaleString('en-US')} likes and comments against ${verifiedViews.toLocaleString('en-US')} views.`,
    evidence: { ...evidence, engagement, ratio: rounded },
  };
}

/**
 * VELOCITY — one bucket step growing by more than the limit.
 *
 * Only steps that START at or after hour 24, because the 6 h → 24 h step is
 * where a genuine hit shows up: the largest honest jump measured there on the
 * fleet was 22.3×, against 3.25× for every later step.
 */
export function evaluateVelocity(
  series: readonly CurvePoint[],
  verifiedViews: number,
  limits: PlatformFraudThresholds,
  minViews: number,
): FraudVerdict {
  const points = measured(series);
  const steps: Array<{ fromHours: number; toHours: number; from: number; to: number; ratio: number }> = [];
  for (let i = 1; i < points.length; i += 1) {
    const from = points[i - 1]!;
    const to = points[i]!;
    if (from.bucketHours < VELOCITY_FIRST_BUCKET_HOURS) continue;
    if ((from.views ?? 0) <= 0) continue;
    steps.push({
      fromHours: from.bucketHours,
      toHours: to.bucketHours,
      from: from.views!,
      to: to.views!,
      ratio: Math.round((to.views! / from.views!) * 1000) / 1000,
    });
  }

  const evidence: Record<string, unknown> = {
    threshold: limits.maxLateBucketGrowthRatio,
    firstBucketHours: VELOCITY_FIRST_BUCKET_HOURS,
    steps,
  };

  if (verifiedViews < minViews) {
    return {
      result: 'PASS',
      note: `Under ${minViews.toLocaleString('en-US')} views, so a jump between readings is noise.`,
      evidence: { ...evidence, skipped: 'below_view_floor' },
    };
  }
  if (steps.length === 0) {
    return {
      result: 'EXCEPTION',
      note: 'There are not two readings after hour 24 to compare, so the jump between them could not be measured.',
      evidence: { ...evidence, skipped: 'not_enough_readings' },
    };
  }

  const worst = steps.reduce((a, b) => (b.ratio > a.ratio ? b : a));
  if (worst.ratio > limits.maxLateBucketGrowthRatio) {
    return {
      result: 'FAIL',
      note:
        `Views went from ${worst.from.toLocaleString('en-US')} at hour ${worst.fromHours} to ` +
        `${worst.to.toLocaleString('en-US')} at hour ${worst.toHours} — ${worst.ratio}× in one step, ` +
        `against a ${limits.maxLateBucketGrowthRatio}× limit.`,
      evidence: { ...evidence, worst },
    };
  }
  return {
    result: 'PASS',
    note: `The largest jump between two readings after hour 24 was ${worst.ratio}×.`,
    evidence: { ...evidence, worst },
  };
}

/** ACCOUNT_AGE — the account was younger than the campaign's floor when it posted. */
export function evaluateAccountAge(input: {
  accountCreatedAt: string | null;
  postPublishedAt: string | null;
  minAccountAgeDays: number;
}): FraudVerdict {
  const evidence: Record<string, unknown> = {
    minAccountAgeDays: input.minAccountAgeDays,
    accountCreatedAt: input.accountCreatedAt,
    postPublishedAt: input.postPublishedAt,
  };
  if (input.minAccountAgeDays <= 0) {
    return {
      result: 'PASS',
      note: 'This campaign sets no minimum account age.',
      evidence: { ...evidence, skipped: 'no_minimum' },
    };
  }
  if (!input.accountCreatedAt || !input.postPublishedAt) {
    /* TikTok and Instagram do not publish an account's creation date at all —
       only YouTube does — so this is the normal answer on two platforms out of
       three whenever a campaign does set a floor. An EXCEPTION holds for a
       person rather than rejecting a creator over a fact nobody can read. */
    return {
      result: 'EXCEPTION',
      note: 'The platform does not publish when this account was created, so its age against the campaign floor could not be checked.',
      evidence: { ...evidence, skipped: 'age_not_published' },
    };
  }
  const ageDays =
    (Date.parse(input.postPublishedAt) - Date.parse(input.accountCreatedAt)) / 86_400_000;
  const rounded = Math.round(ageDays * 10) / 10;
  if (!Number.isFinite(ageDays)) {
    return {
      result: 'EXCEPTION',
      note: 'The account creation date could not be read as a date.',
      evidence: { ...evidence, skipped: 'unparseable' },
    };
  }
  if (ageDays < input.minAccountAgeDays) {
    return {
      result: 'FAIL',
      note: `The account was ${rounded} days old when this went up, against this campaign's ${input.minAccountAgeDays}-day minimum.`,
      evidence: { ...evidence, accountAgeDays: rounded },
    };
  }
  return {
    result: 'PASS',
    note: `The account was ${rounded} days old when this went up.`,
    evidence: { ...evidence, accountAgeDays: rounded },
  };
}

/** CONCENTRATION — one creator holding too much of a campaign's verified views. */
export function evaluateConcentration(input: {
  creatorVerifiedViews: number;
  campaignVerifiedViews: number;
  creatorsWithCalculations: number;
  maxCreatorViewShare: number;
  concentrationMinCreators: number;
}): FraudVerdict {
  const evidence: Record<string, unknown> = {
    threshold: input.maxCreatorViewShare,
    creatorVerifiedViews: input.creatorVerifiedViews,
    campaignVerifiedViews: input.campaignVerifiedViews,
    creatorsWithCalculations: input.creatorsWithCalculations,
    concentrationMinCreators: input.concentrationMinCreators,
  };
  if (input.creatorsWithCalculations < input.concentrationMinCreators) {
    return {
      result: 'PASS',
      note: `Only ${input.creatorsWithCalculations} creator${input.creatorsWithCalculations === 1 ? '' : 's'} have finished a post on this campaign, so nobody's share means anything yet.`,
      evidence: { ...evidence, skipped: 'too_few_creators' },
    };
  }
  if (input.campaignVerifiedViews <= 0) {
    return { result: 'PASS', note: 'The campaign has no verified views yet.', evidence };
  }
  const share = input.creatorVerifiedViews / input.campaignVerifiedViews;
  const rounded = Math.round(share * 10_000) / 10_000;
  if (share > input.maxCreatorViewShare) {
    return {
      result: 'FAIL',
      note: `This creator holds ${pct(share)} of the campaign's verified views, above the ${pct(input.maxCreatorViewShare)} limit.`,
      evidence: { ...evidence, share: rounded },
    };
  }
  return {
    result: 'PASS',
    note: `This creator holds ${pct(share)} of the campaign's verified views.`,
    evidence: { ...evidence, share: rounded },
  };
}

/** HIGH_VALUE — a single post big enough that a person looks at it. */
export function evaluateHighValue(verifiedViews: number, holdAbove: number): FraudVerdict {
  const evidence = { threshold: holdAbove, verifiedViews };
  if (verifiedViews >= holdAbove) {
    return {
      result: 'FAIL',
      note: `${verifiedViews.toLocaleString('en-US')} verified views is at or above this campaign's ${holdAbove.toLocaleString('en-US')} review threshold, so it waits for a person.`,
      evidence,
    };
  }
  return {
    result: 'PASS',
    note: `${verifiedViews.toLocaleString('en-US')} verified views is below this campaign's ${holdAbove.toLocaleString('en-US')} review threshold.`,
    evidence,
  };
}

/* ------------------------------------------------------------------ */
/* Names                                                               */
/* ------------------------------------------------------------------ */

/**
 * The plan's name for each fraud rule, and the column value it is stored under.
 *
 * TWO RULES SHARE ONE STORED VALUE, and it is worth saying why out loud. The
 * `VerificationCheck.checkType` set is pinned by a CHECK constraint ported from
 * the Postgres scaffold, and the scaffold has one value —
 * `NEW_ACCOUNT_CONCENTRATION` — for what the plan writes as "new-account
 * concentration". This brief splits that into two rules with different
 * evidence: how old the account was, and how much of the campaign this creator
 * holds. Adding a distinct `ACCOUNT_AGE` value would mean REBUILDING the
 * VerificationCheck table (SQLite cannot alter a CHECK), and migration 0004 is
 * add-column only against a live database. So both rules are stored under the
 * scaffold's value and told apart by `planName`, which this codebase already
 * writes into every check's evidence for exactly this purpose — see
 * `SUBMISSION_CHECKS` in `./submissions.ts`, which draws the same line between
 * the database's names and the plan's.
 */
export const FRAUD_CHECKS = Object.freeze([
  { planName: 'CURVE_SHAPE', checkType: 'CURVE_SHAPE', label: 'The shape of the view curve' },
  {
    planName: 'ENGAGEMENT_RATIO',
    checkType: 'ENGAGEMENT_RATIO',
    label: 'Likes and comments per view',
  },
  { planName: 'VELOCITY', checkType: 'VELOCITY_SPIKE', label: 'The jump between two readings' },
  {
    planName: 'ACCOUNT_AGE',
    checkType: 'NEW_ACCOUNT_CONCENTRATION',
    label: "The account's age against the campaign floor",
  },
  {
    planName: 'CONCENTRATION',
    checkType: 'NEW_ACCOUNT_CONCENTRATION',
    label: "This creator's share of the campaign",
  },
  {
    planName: 'HIGH_VALUE',
    checkType: 'SINGLE_POST_THRESHOLD',
    label: 'A post big enough for a person to see',
  },
] as const);

export type FraudCheckName = (typeof FRAUD_CHECKS)[number]['planName'];

/* ------------------------------------------------------------------ */
/* Which checks a set of thresholds has switched off                   */
/* ------------------------------------------------------------------ */

/**
 * The rules a threshold set can no longer refuse anything with.
 *
 * Every threshold has a legal value that disables its rule without looking like
 * it: `maxLateViewShare: 1` can never be exceeded because a share cannot exceed
 * one, `minEngagementPerView: 0` can never be undercut, a
 * `minViewsForRatioChecks` above any real post skips all three ratio rules, and
 * `holdAboveVerifiedViews` at a billion is a review threshold nothing reaches.
 * None of that is invalid — an operator may genuinely want a rule off on a
 * campaign — but it must never happen QUIETLY, because "the checks ran and
 * passed" and "the checks could not fail" read identically in the evidence.
 * `POST /admin/campaigns/:id` names every rule this answer lists in its own
 * audit event.
 */
export function disabledFraudChecks(thresholds: FraudThresholds): string[] {
  const off: string[] = [];
  const ratioFloor = thresholds.minViewsForRatioChecks;
  if (ratioFloor >= 1_000_000) {
    off.push(`CURVE_SHAPE, ENGAGEMENT_RATIO and VELOCITY (view floor ${ratioFloor})`);
  } else {
    for (const platform of PLATFORM_KEYS) {
      const limits = thresholds.perPlatform[platform];
      if (limits.maxLateViewShare >= 1) off.push(`CURVE_SHAPE on ${platform}`);
      if (limits.minEngagementPerView <= 0) off.push(`ENGAGEMENT_RATIO on ${platform}`);
    }
  }
  if (thresholds.maxCreatorViewShare >= 1) off.push('CONCENTRATION');
  if (thresholds.holdAboveVerifiedViews >= 1_000_000_000) off.push('HIGH_VALUE');
  return off;
}
